import type { OnderwerpRij } from '@vip/shared';

/**
 * Zoeken in de taxonomie, over twee lagen.
 *
 * De regel die het bruikbaar maakt: raakt de zoekterm het hoofdonderwerp, dan
 * krijg je het hele blok. Raakt hij alleen deelonderwerpen, dan krijg je alleen
 * die. Zou een treffer op een deelonderwerp het hele blok opleveren, dan lees je
 * alsnog langs elf dingen die je niet zocht; en zou een treffer op het
 * hoofdonderwerp zijn blok wegfilteren, dan zou "ABS" een leeg resultaat geven
 * terwijl je precies dat onderwerp bedoelde.
 *
 * Elk getypt woord moet voorkomen, in willekeurige volgorde, en voor een
 * deelonderwerp mag dat ook in de naam van zijn hoofdonderwerp. Zo vindt
 * "abs parent" de parent-childregels onder ABS zonder dat je het hele pad
 * uitschrijft.
 */
export function filterTaxonomie(
  onderwerpen: readonly OnderwerpRij[],
  zoekterm: string,
): OnderwerpRij[] {
  const woorden = zoekterm.toLowerCase().split(/\s+/).filter(Boolean);
  if (woorden.length === 0) return [...onderwerpen];

  const uit: OnderwerpRij[] = [];
  for (const o of onderwerpen) {
    const onderwerpNaam = o.naam.toLowerCase();
    if (woorden.every((w) => onderwerpNaam.includes(w))) {
      uit.push(o);
      continue;
    }
    const raken = o.deelonderwerpen.filter((d) => {
      const tekst = `${onderwerpNaam} ${d.naam.toLowerCase()}`;
      return woorden.every((w) => tekst.includes(w));
    });
    if (raken.length > 0) uit.push({ ...o, deelonderwerpen: raken });
  }
  return uit;
}

/** Hoeveel deelonderwerpen er in beeld staan. Dat is wat je telt: het aantal
 *  hoofdonderwerpen zegt niets over hoeveel je hebt gevonden. */
export const telDeelonderwerpen = (onderwerpen: readonly OnderwerpRij[]) =>
  onderwerpen.reduce((n, o) => n + o.deelonderwerpen.length, 0);

/**
 * De volgorde van de lijst: bovenaan waar het meeste aan hangt.
 *
 * Het totaal komt hier vandaan en niet uit het blok zelf, zodat de volgorde en
 * het getal achter de naam niet uiteen kunnen lopen. Bij een gelijk aantal
 * beslist de naam, anders verspringt de lijst bij elke keer openen.
 */
export interface GesorteerdOnderwerp {
  onderwerp: OnderwerpRij;
  totaal: number;
}

export function sorteerOpDrukte(onderwerpen: readonly OnderwerpRij[]): GesorteerdOnderwerp[] {
  return onderwerpen
    .map((onderwerp) => ({
      onderwerp,
      totaal: onderwerp.deelonderwerpen.reduce((n, d) => n + d.aantal, 0),
    }))
    .sort((a, b) => b.totaal - a.totaal || a.onderwerp.naam.localeCompare(b.onderwerp.naam, 'nl'));
}
